const SCRIPT_ID = 'zohodeskasapscript';
const BTN_SELECTOR_ID = '#zohohc-asap-web-button';
const STYLE_OVERRIDE_ID = 'zoho-desk-asap-override';
const ASAP_APP_SRC = 'https://desk.zoho.com/portal/api/web/asapApp/628495000005320009?orgId=756533248';

/**
 * The composable is instantiated by every layout plus a few components, so several instances are alive at once and each
 * one registers its own route watcher. The loader and the launcher visibility are process-wide concerns, so they are
 * tracked here at module scope: the bootstrap is injected once and the visibility work only runs when it changes.
 */
let appliedVisibility: boolean | null = null;

/**
 * The CSP nonce this document was served with, if any.
 *
 * The bootstrap reads the nonce back off its own script tag and copies it onto every stylesheet and bundle it injects
 * afterwards, so the widget only loads under a Content-Security-Policy if the nonce is on the script we insert. The app
 * currently serves no CSP, so there is nothing to find and the attribute is left off — the placeholder in the snippet
 * Jordan sent is filled in server-side on the marketing site, and hardcoding it here would hand the SDK an invalid
 * nonce the day a policy is introduced. Reading it from the page instead keeps working either way.
 */
const resolveNonce = () => {
    // Not `querySelector('script[nonce]')`, which is how the SDK looks it up: browsers blank out the nonce content
    // attribute once a policy is being enforced, and from then on only the IDL property still holds the value.
    for (const script of Array.from(document.getElementsByTagName('script'))) {
        const nonce = script.nonce || script.getAttribute('nonce');
        if (nonce) {
            return nonce;
        }
    }

    return '';
};

/**
 * Injects the ASAP bootstrap script once, and installs the callback queue the SDK expects alongside it.
 *
 * The bootstrap only renders the floating launcher; the widget itself (i18n, vendor, react and efc bundles) is loaded
 * in a second stage, either when the launcher is clicked or five seconds later. `ZohoDeskAsap.invoke` and
 * `window.ZohoDeskAsapReadyStatus` only exist once that stage has mounted, which is what `ZohoDeskAsapReady` is for.
 */
const ensureScriptInjected = () => {
    if (document.getElementById(SCRIPT_ID)) {
        return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = SCRIPT_ID;
    script.defer = true;
    script.src = ASAP_APP_SRC;

    const nonce = resolveNonce();
    if (nonce) {
        script.setAttribute('nonce', nonce);
    }

    // The SDK appends its own resources to <head>, so the bootstrap goes there too. Anchoring the insert to the first
    // <script> in the document, as the snippet does, would skip the injection entirely on a page that has none yet.
    (document.head ?? document.getElementsByTagName('head')[0])?.appendChild(script);

    // Queues callbacks until the widget has mounted. The bootstrap replaces this with its own identical implementation
    // once it runs, so it is only installed while it is missing: the SDK's copy must win if it got there first.
    if (typeof window.ZohoDeskAsapReady !== 'function') {
        window.ZohoDeskAsapReady = function (callback) {
            const asyncCalls = window.ZohoDeskAsap__asyncalls = window.ZohoDeskAsap__asyncalls || [];
            if (window.ZohoDeskAsapReadyStatus) {
                callback && asyncCalls.push(callback);
                asyncCalls.forEach((queued) => queued && queued());
                window.ZohoDeskAsap__asyncalls = null;
            } else {
                callback && asyncCalls.push(callback);
            }
        };
    }
};

/**
 * Adds or removes the stylesheet that hides the launcher.
 *
 * A stylesheet is used because the SDK sets `display` inline on the button, and an `!important` rule from a stylesheet
 * is the only thing that outranks it. It has to be applied before the bootstrap is injected as well as on later route
 * changes, which is why it does not live in the script's `onload` handler.
 */
const setStyleOverride = (hidden: boolean) => {
    const existing = document.getElementById(STYLE_OVERRIDE_ID);

    if (!hidden) {
        existing?.remove();
        return;
    }

    if (existing) {
        return;
    }

    const styleElement = document.createElement('style');
    styleElement.id = STYLE_OVERRIDE_ID;
    styleElement.textContent = `${BTN_SELECTOR_ID} { display: none !important; }`;

    const nonce = resolveNonce();
    if (nonce) {
        styleElement.setAttribute('nonce', nonce);
    }

    document.head.appendChild(styleElement);
};

/**
 * This composable provides wrapper methods to interact with the Zoho Desk ASAP widget. For full JS API, see: https://www.zoho.com/desk/developers/asap/
 * @returns An object with methods to open, close, and toggle the visibility of the Zoho Desk ASAP button.
 */
export const useZohoDeskAsap = () => {

    const route = useRoute();

    /**
     * This method toggles the visibility of the Zoho Desk ASAP button.
     * @param show - Whether to show the button.
     */
    const toggleButtonVisibility = (show = true) => {
        try {
            appliedVisibility = show;
            setStyleOverride(!show);

            // Only available once the widget has mounted; the stylesheet above is what actually does the work.
            if (typeof window?.ZohoDeskAsap?.invoke === 'function') {
                window.ZohoDeskAsap.invoke(show ? 'show' : 'hide', 'app.launcher');
            }
        } catch (error) {
            console.error('Error toggling button visibility:', error);
        }
    };

    /**
     * This method opens the Zoho Desk ASAP widget. If the widget has not mounted yet the call is queued and runs as
     * soon as it has, instead of being silently dropped.
     */
    const open = () => {
        try {
            ensureScriptInjected();
            window.ZohoDeskAsapReady?.(() => window?.ZohoDeskAsap?.invoke('open'));
        } catch (error) {
            console.error('Error opening Zoho Desk ASAP widget:', error);
        }
    };

    /**
     * This method closes the Zoho Desk ASAP widget. Unlike `open` it is not queued: there is nothing to close while the
     * widget is still loading.
     */
    const close = () => {
        try {
            if (typeof window?.ZohoDeskAsap?.invoke === 'function') {
                window.ZohoDeskAsap.invoke('close');
            }
        } catch (error) {
            console.error('Error closing Zoho Desk ASAP widget:', error);
        }
    };

    /**
     * This method initializes the Zoho Desk ASAP widget and keeps the launcher visibility in sync with the current
     * route. This is done only in the client side. This is the code sent by Jordan to implement it, adapted to run
     * from a composable rather than as an inline tag.
     */
    const init = (toggled?: boolean) => {
        const shouldShow = toggled ?? true;

        // Applied before the injection so the launcher is never visible for a few frames on a route that hides it.
        if (appliedVisibility !== shouldShow) {
            toggleButtonVisibility(shouldShow);
        }

        ensureScriptInjected();
    };

    watch(() => route.meta.asapEnabled, (newVal: unknown) => {
        if (process.client) {
            init(newVal as boolean|undefined);
        }
    }, { immediate: true });

    return { open, close, toggleButtonVisibility };
};
